import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileReady, setProfileReady] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId, options = {}) {
      if (!userId) return
      const skipA2pPoll = options.skipA2pPoll === true
      if (!skipA2pPoll) setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) {
          setUserProfile(data)
          if (!skipA2pPoll) {
            supabase?.auth?.getSession()?.then(({ data: { session } }) => {
              if (!session?.access_token) return
              fetch('/api/a2p/status?refresh=1', { headers: { Authorization: `Bearer ${session.access_token}` } })
                .finally(() => profileOperations.load(userId, { skipA2pPoll: true }))
            })
          }
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        if (!skipA2pPoll) {
          setProfileLoading(false)
          setProfileReady(true)
        }
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      setProfileReady(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    console.log('🟡 AuthContext signIn called', { email, supabase: !!supabase });
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      console.log('🟡 Supabase response:', { data, error });
      return { data, error }
    } catch (error) {
      console.error('🔴 signIn exception:', error);
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'agent'
          }
        }
      })
      return { data, error }
    } catch (error) {
      console.error('🔴 signUp exception:', error);
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    profileReady,
    signIn,
    signUp,
    signOut,
    updateProfile,
    reloadProfile: () => profileOperations.load(user?.id),
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
